import React from 'react';
import ModalDialog from '../../../organisms/ModalDialog/ModalDialog';
import { Namespace } from '../../../../translation/namespace';
import { TeamroomView } from '../TeamroomManagement.types';
import { RequestActivityForm } from '../forms/RequestActivityForm';
import { TeamroomInviteView } from '../../../forms/TeamroomInvite/TeamroomInvite';
import { AcceptDeclineForm } from '../../../../_lib/Form2';
import { useDelayed } from '../../../../hooks/useDelayed';
import { useBlockContext, useRefreshBlock } from '../../../../_lib/BlockFetcher';
import { ACTIVATION_VIEW_KEYS, BUTTON_CONFIGS } from '../TeamroomManagement.constants';
import useRedirect from '../../../../hooks/useRedirect';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Hint } from '../../../../_lib/components/Hint';
import { TeamroomInvitationView } from './TeamroomInvitationView';

type TeamroomManagementActiveViewProps = {
  currentActiveView: TeamroomView | null;
  views: Record<string, unknown> | null | undefined;
  closeView: () => void;
};

export function TeamroomManagementActiveView({
  currentActiveView,
  views,
  closeView,
}: TeamroomManagementActiveViewProps) {
  const { t } = useSonTranslation(Namespace.TeamroomManagement);
  const { uuid, elementType } = useBlockContext();
  const [startRedirect] = useRedirect();
  const { refresh } = useRefreshBlock();
  const delayedRefresh = useDelayed(refresh);
  const blockId = `${elementType}-${uuid}`;

  const invitationView = views?.invitation as Record<string, unknown> | undefined;
  const hasInvitation = !!invitationView;

  const hasCollapsibleView = BUTTON_CONFIGS.some(config => !config.isModal && views && views[config.key]);

  if (!hasCollapsibleView && !currentActiveView && !hasInvitation) {
    return null;
  }

  const getInvitationView = () => {
    if (!invitationView) return null;

    const form = (invitationView.embedded as { form?: unknown })?.form as never;

    return (
      <TeamroomInvitationView
        form={form}
        headline={t('actions.invitation.text')}
        messageSuccess={t('actions.invitation.approve.success')}
        messageFail={t('actions.invitation.approve.fail')}
        messageRejectSuccess={t('actions.invitation.reject.success')}
        messageRejectFail={t('actions.invitation.reject.fail')}
        messageSending={t('actions.invitation.approve.sending')}
      />
    );
  };

  const invitationContent = hasInvitation ? getInvitationView() : null;

  let actionContent: React.ReactNode = null;

  if (currentActiveView && views?.[currentActiveView]) {
    const view = views[currentActiveView];

    const getActionView = (viewKey: TeamroomView, viewData: unknown) => {
      const viewRecord = viewData as Record<string, unknown>;
      switch (viewKey) {
        case TeamroomView.INVITE:
        case TeamroomView.INVITE_UNREGISTERED:
          return (
            <TeamroomInviteView
              additionalProps={{
                translationNamespace: `${Namespace.TeamroomManagement}:actions.${viewKey}`,
              }}
              {...(viewRecord as any)}
            />
          );
        case TeamroomView.LEAVE:
          return (
            <AcceptDeclineForm
              form={(viewRecord.embedded as { form?: unknown })?.form as never}
              headline={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.question`)}
              messageFail={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.fail`)}
              messageSending={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.sending`)}
              messageSuccess={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.success`)}
              onDecline={closeView}
              onSuccess={(response) => {
                if (response?.data?.url) {
                  startRedirect(response.data.url);
                }
              }}
              customErrorTypeMessages={{
                teamroomCoordinatorIsRequired: t('actions.leave.status.teamroomCoordinatorIsRequired'),
              }}
            >
              <Hint message={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.hint`)} />
            </AcceptDeclineForm>
          );
        case TeamroomView.JOIN:
        case TeamroomView.JOIN_REQUEST:
          return (
            <AcceptDeclineForm
              form={(viewRecord.embedded as { form?: unknown })?.form as never}
              headline={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.question`)}
              messageFail={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.fail`)}
              messageSending={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.sending`)}
              messageSuccess={t(`${Namespace.TeamroomManagement}:actions.${viewKey}.status.success`)}
              onDecline={closeView}
              onSuccess={delayedRefresh}
            />
          );
        default:
          return null;
      }
    };

    actionContent = ACTIVATION_VIEW_KEYS.has(currentActiveView)
      ? (
          <RequestActivityForm
            form={(view as { embedded?: { form?: unknown } })?.embedded?.form as never}
            translationNamespace={`${Namespace.TeamroomManagement}:actions.${currentActiveView}`}
            refreshBlock={refresh}
          />
        )
      : (
          getActionView(currentActiveView, view)
        );
  }

  const config = BUTTON_CONFIGS.find(c => c.key === currentActiveView);

  if (config?.isModal && currentActiveView && actionContent) {
    return (
      <>
        {invitationContent}
        <ModalDialog
          closeModal={closeView}
          headline={t(`${Namespace.TeamroomManagement}:actions.${currentActiveView}.text`)}
          initialFocusIndex={config?.initialFocusIndex ?? 1}
        >
          {actionContent}
        </ModalDialog>
      </>
    );
  }

  const hasContent = invitationContent || actionContent;

  return (
    <div
      id={`block-content-${blockId}`}
      className="teamroom-management__view-container"
      aria-hidden={!hasContent}
    >
      {invitationContent}
      {actionContent}
    </div>
  );
}
